import { useEffect } from 'react';

import { APP_VERSION } from '../api';
import { getBridge } from '../bridge';
import { useRouter } from '../router';
import { selectSid, useStore } from '../store';
import { getLiveClient } from './client';
import { subscribeConnection } from './connection';
import { useRemoteOpenPlayer } from './openPlayer';
import { trackPresence } from './presence';
import { subscribeSession } from './session';
import { endLiveSession, installApiSessionGuard } from './sessionEnd';
import { setTvStateErrorHandler } from './tvState';

function log(level: 'warn' | 'error', message: string): void {
  getBridge(APP_VERSION).bridge.log(level, message);
}

/** `.info/connected` → `ui.connected` / `ui.reconnecting` (docs/04 §5). */
export function useConnectionLive(): void {
  const authMode = useStore((s) => s.authMode);
  useEffect(() => {
    const client = getLiveClient();
    if (!client) return undefined;
    return subscribeConnection(client, useStore);
  }, [authMode]);
}

/** Subscribes the store to `meta` / `queue` / `player` / `members` for as long as `sid` lives. */
export function useSessionLive(sid: string | null): void {
  const authMode = useStore((s) => s.authMode);
  useEffect(() => {
    if (!sid) return undefined;
    const client = getLiveClient();
    if (!client) return undefined;
    return subscribeSession(client, sid, useStore, {
      onSessionClosed: (reason) => endLiveSession(reason),
      onNodeError: (path, error) => log('warn', `live ${path}: ${error.message}`),
    });
  }, [sid, authMode]);
}

/**
 * Own presence under `members/{uid}` (docs/02 §2). Only mounted for a uid that is actually a
 * member: a TV token has no member node, so the rules would reject its writes.
 */
export function usePresence(sid: string | null, uid: string | null): void {
  const isMember = useStore((s) => (uid ? Boolean(s.members[uid]) : false));
  const authMode = useStore((s) => s.authMode);
  useEffect(() => {
    if (!sid || !uid || !isMember) return undefined;
    const client = getLiveClient();
    if (!client) return undefined;
    return trackPresence(client, sid, uid, {
      onError: (error) => log('warn', `presence: ${error.message}`),
    });
  }, [sid, uid, isMember, authMode]);
}

/** Everything the shell needs in one call. */
export function useLive(): void {
  const sid = useStore(selectSid);
  const uid = useStore((s) => s.auth.uid);

  useEffect(() => {
    installApiSessionGuard();
    setTvStateErrorHandler((error) => log('warn', `tvState: ${error.message}`));
  }, []);

  useConnectionLive();
  useSessionLive(sid);
  usePresence(sid, uid);
  // docs/04 §4.4: a remote `playItem` opens the player even when the TV is not on it.
  useRemoteOpenPlayer(useRouter);
}
